import vid from "../../assets/video/archidica-home-scroll.mp4.asset.json";
import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/archidica";

const img = (
  file: string,
  alt: string,
  width: number,
  height: number,
  span: 2 | 3 | 6 = 3,
  small = true,
) => ({
  src: `${dir}/${file}.webp`,
  ...(small ? { src900: `${dir}/${file}-900.webp` } : {}),
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

export const archidica: CaseStudy = {
  slug: "archidica",
  title: "Archidica",
  cardTitle: "Сайт-портфолио для архитектурного бюро Archidica",
  subtitle: "Архитектурно-дизайнерское бюро полного цикла",
  industry: { label: "Архитектура и дизайн", slug: "architecture" },
  eyebrow: "Корпоративный сайт · Архитектура и дизайн",
  h1: "Разработка сайта-портфолио для архитектурного бюро Archidica",
  lead:
    "Создали масштабируемую цифровую платформу для архитектурно-дизайнерского бюро: с визуальным портфолио, фильтрацией проектов, отраслевыми посадочными страницами и формами заявок для частных заказчиков и бизнеса.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить похожий проект" },

  seo: {
    primaryKeyword: "разработка сайта-портфолио для архитектурного бюро",
    secondaryKeywords: [
      "разработка сайта для архитектурного бюро",
      "создание сайта архитектурного бюро",
      "корпоративный сайт для архитектурного бюро",
      "сайт-портфолио архитектурного бюро",
      "сайт для архитектурной студии",
      "разработка сайта для дизайн-студии интерьера",
      "создание сайта для студии дизайна",
      "сайт для архитектурной компании",
      "разработка сайта на 1С-Битрикс для архитектурного бюро",
      "SEO-структура сайта архитектурного бюро",
    ],
    semanticEntities: [
      "архитектура",
      "дизайн интерьера",
      "архитектурное бюро",
      "проектирование",
      "портфолио проектов",
      "частные проекты",
      "коммерческие проекты",
      "виллы",
      "квартиры",
      "отели",
      "рестораны",
      "бизнес-центры",
      "банные комплексы",
      "SPA-комплексы",
      "каталог проектов",
      "фильтрация",
      "индивидуальные страницы проектов",
      "отраслевая экспертиза",
      "приглашение в тендер",
      "1С-Битрикс",
      "SEO-архитектура",
      "адаптивная версия",
      "аналитика",
    ],
    excludedKeywords: [
      "архитектурное бюро Москва",
      "заказать дизайн интерьера",
      "дизайн квартиры",
      "архитектурный проект дома",
      "проектирование коттеджа",
      "проектирование отеля",
      "дизайн ресторана",
      "архитектор Москва",
      "стоимость дизайна интерьера",
    ],
    title: "Разработка сайта архитектурного бюро Archidica — кейс HAY",
    description:
      "Кейс HAY.agency: разработка сайта-портфолио Archidica на 1С-Битрикс. Каталог проектов, фильтры, отраслевые страницы, формы заявок и SEO-структура.",
    h1: "Разработка сайта-портфолио для архитектурного бюро Archidica",
    slug: "archidica",
    canonicalUrl: `${SITE_URL}/cases/archidica`,
    ogTitle: "Сайт-портфолио архитектурного бюро Archidica",
    ogDescription:
      "Как мы объединили большое портфолио, отраслевую экспертизу и SEO-посадочные страницы в одной цифровой системе.",
    ogImage: `${SITE_URL}${dir}/archidica-og.webp`,
    robotsIndex: true,
    datePublished: "2024-06-01",
    dateModified: "2026-08-28",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Основная услуга проекта: структура, дизайн и разработка сайта-портфолио",
    },
    relatedIndustry: {
      label: "SEO-продвижение сайтов услуг",
      to: routes.seo,
      note: "Отраслевые посадочные страницы и поисковая структура проекта",
    },
    relatedCases: [
      {
        label: "Сайт-каталог для производителя заборов Artline",
        to: "/cases/artline",
        note: "Проект со сложным каталогом продукции и сравнением комплектаций",
      },
      {
        label: "Сайт для компании по строительству домов ESG",
        to: routes.corporate,
        note: "Строительный бизнес с большим визуальным портфолио объектов",
      },
      {
        label: "Сайт банного комплекса «Покровские бани»",
        to: routes.landing,
        note: "Объект с насыщенным визуальным контентом и записью через сайт",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт для архитектурного бюро или дизайн-студии?",
    text:
      "Создадим структуру, которая покажет портфолио, разделит частные и коммерческие направления и подготовит сайт к SEO-продвижению.",
    button: "Обсудить сайт",
  },

  client: {
    name: "Archidica",
    url: "https://archidica.com/",
    city: "Уточняется",
    industry: "Архитектура и дизайн интерьеров",
  },
  year: "Уточняется",
  services: [
    "Аналитика и проектирование",
    "UX/UI-дизайн",
    "Разработка корпоративного сайта",
    "Разработка на 1С-Битрикс",
    "SEO-архитектура",
    "SEO-продвижение",
    "Техническая поддержка",
    "Веб-аналитика",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Корпоративные сайты", to: routes.corporate },
    { label: "SEO-продвижение", to: routes.seo },
    { label: "Разработка на Битрикс", to: routes.bitrix24 },
  ],

  facts: [
    { label: "Клиент", value: "Archidica" },
    { label: "Отрасль", value: "Архитектура и дизайн интерьеров" },
    { label: "Тип проекта", value: "Корпоративный сайт-портфолио" },
    { label: "География", value: "Указываем после подтверждения с клиентом" },
    { label: "Платформа", value: "1С-Битрикс" },
    {
      label: "Услуги",
      value:
        "Аналитика, проектирование, UX/UI-дизайн, разработка, SEO-архитектура, продвижение",
    },
    {
      label: "Аудитория",
      value: "Частные заказчики, девелоперы, инвесторы и представители бизнеса",
    },
    { label: "Год запуска", value: "Указываем после подтверждения" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог проектов с фильтрацией по формату, типу работ и категории объекта.",
      "Отдельные индексируемые страницы под каждый объект портфолио.",
      "Отраслевые и сервисные посадочные страницы под проектирование и дизайн интерьеров.",
      "Разные сценарии обращения: консультация, встреча, предложение и приглашение в тендер.",
    ],
  },

  about: {
    title: "Архитектурно-дизайнерское бюро полного цикла",
    paragraphs: [
      "Archidica работает на пересечении архитектуры, дизайна интерьеров и комплексного проектирования. Бюро создаёт частные и коммерческие пространства: виллы, квартиры, отели, рестораны, бизнес-центры, банные и термальные комплексы.",
      "Большое и постоянно пополняемое портфолио требовало сайта, который эффектно представляет проекты и помогает разным категориям заказчиков быстро находить релевантный опыт бюро.",
    ],
    images: [
      img("archidica-villas", "Раздел частной архитектуры и вилл на сайте Archidica", 1920, 1246),
      img("archidica-interiors", "Раздел интерьерных проектов на сайте Archidica", 1920, 1246),
      img(
        "archidica-commercial-architecture",
        "Раздел коммерческой недвижимости на сайте архитектурного бюро Archidica",
        1920,
        1246,
      ),
      img(
        "archidica-project-agalarov",
        "Страница проекта загородной виллы в портфолио Archidica",
        1920,
        1246,
      ),
    ],
  },

  challenge: {
    title: "Превратить большое портфолио в систему привлечения заявок",
    paragraphs: [
      "Обычная галерея изображений не могла решить задачу Archidica. Пользователи приходят на сайт с разными запросами: кто-то ищет архитектора для частного дома, кто-то — проектировщика гостиницы, ресторана или коммерческого комплекса.",
      "Нужно было создать понятную информационную архитектуру, сохранить эмоциональную подачу проектов и одновременно подготовить сайт к масштабированию и SEO-продвижению.",
    ],
    steps: [
      {
        title: "Структурировать портфолио",
        text: "Разделить частные и коммерческие проекты, архитектуру и интерьеры, а также разные типы объектов.",
      },
      {
        title: "Передать уровень бюро",
        text: "Сделать проекты главным визуальным элементом сайта, не перегружая интерфейс декоративными решениями.",
      },
      {
        title: "Построить SEO-архитектуру",
        text: "Создать отдельные страницы под отрасли, виды проектирования и направления дизайна интерьера.",
      },
      {
        title: "Создать точки конверсии",
        text: "Разделить сценарии частных заказчиков, представителей бизнеса и компаний, приглашающих бюро к участию в тендерах.",
      },
    ],
  },

  columnBlocks: [
    {
      id: "architecture",
      title: "Не просто портфолио, а цифровая архитектура проектного бизнеса",
      paragraphs: [
        "Структуру сайта построили вокруг трёх взаимосвязанных уровней: репутации, портфолио и привлечения спроса. Пользователь может начать знакомство с любого раздела, увидеть релевантные проекты, изучить экспертизу бюро и перейти к обращению.",
      ],
      columns: [
        {
          title: "Уровень 1. Бренд и доверие",
          items: [
            "Главная",
            "О бюро",
            "Команда",
            "Клиенты",
            "СМИ о бюро",
            "Специальные предложения",
            "Контакты",
          ],
        },
        {
          title: "Уровень 2. Портфолио",
          items: [
            "Виллы",
            "Интерьеры",
            "Коммерческая недвижимость",
            "Коммерческие интерьеры",
            "Каталог проектов",
            "Индивидуальные страницы объектов",
          ],
        },
        {
          title: "Уровень 3. Привлечение спроса",
          items: [
            "Отраслевые решения",
            "Страницы проектирования",
            "Страницы дизайна интерьера",
            "Формы консультации",
            "Приглашение в тендер",
            "Запись на встречу",
          ],
        },
      ],
      images: [
        img(
          "archidica-home-competencies",
          "Главная страница сайта архитектурного бюро Archidica",
          1920,
          1246,
          6,
        ),
      ],
    },
    {
      id: "catalog",
      title: "Каталог, в котором легко найти релевантный опыт",
      paragraphs: [
        "Для большого портфолио разработали систему фильтрации по нескольким параметрам. Посетителю не нужно просматривать десятки нерелевантных объектов: он может сразу выбрать нужный сегмент, направление и тип проекта.",
      ],
      columns: [
        { title: "Формат проекта", items: ["Частный", "Коммерческий"] },
        { title: "Тип работы", items: ["Архитектура", "Интерьер"] },
        {
          title: "Категория объекта",
          items: [
            "Вилла",
            "Квартира",
            "Гостиница",
            "Ресторан",
            "Офис",
            "Бизнес-центр",
            "Банный комплекс",
            "SPA-комплекс",
            "Фудмолл",
            "Бутик",
            "Клиника",
            "Промышленный объект",
          ],
        },
      ],
      images: [
        img(
          "archidica-project-filters",
          "Фильтры проектов на сайте Archidica",
          1920,
          1246,
        ),
        img(
          "archidica-home-projects",
          "Каталог архитектурных проектов Archidica",
          1920,
          1246,
        ),
      ],
      imageCols: 2,
      caption:
        "Выбранные параметры сохраняются в адресе страницы, поэтому подборку по типу объекта можно отправить заказчику ссылкой.",
    },
    {
      id: "project-page",
      title: "Каждый объект получил собственную страницу",
      paragraphs: [
        "Проекты вынесли на отдельные индексируемые страницы. На каждой странице пользователь видит название объекта, краткое описание, принадлежность к категориям, визуализации проекта и материалы, показывающие процесс работы.",
      ],
      bullets: [
        "Название проекта и краткое описание объекта",
        "Тип и категория: частный или коммерческий, архитектура или интерьер",
        "Переключение между результатом проекта и процессом работы",
        "Полноэкранная галерея визуализаций архитектуры и интерьера",
        "Переходы к связанным направлениям портфолио",
        "CTA для обращения в бюро прямо со страницы объекта",
      ],
      images: [
        img(
          "archidica-project-agalarov",
          "Страница архитектурного проекта Archidica",
          1920,
          1246,
        ),
        img(
          "archidica-project-kamenka",
          "Галерея визуализаций на странице проекта Archidica",
          1920,
          1246,
        ),
      ],
      imageCols: 2,
      caption:
        "Такой подход превращает портфолио в самостоятельный источник поискового трафика по типам объектов и направлениям проектирования.",
    },
    {
      id: "seo-structure",
      title: "Отдельные посадочные страницы под каждое направление",
      paragraphs: [
        "Главную страницу не пытались оптимизировать одновременно под все услуги бюро. Вместо этого создали отдельные страницы под отрасли, типы объектов, архитектурное проектирование и дизайн интерьеров.",
        "Каждая страница может иметь собственный поисковый кластер, структуру, портфолио, аргументацию и форму обращения.",
      ],
      columns: [
        {
          title: "Уровень 1. Отраслевые решения для бизнеса",
          items: ["Отели", "Банные и термальные комплексы", "Рестораны"],
        },
        {
          title: "Уровень 2. Архитектурное проектирование",
          items: [
            "Коттеджи",
            "Банные комплексы",
            "Рестораны",
            "Торговые центры",
            "Бизнес-центры",
            "Промышленные объекты",
            "Загородные комплексы",
            "Коттеджные посёлки",
            "Развлекательные центры",
            "Спортивные сооружения",
          ],
        },
        {
          title: "Уровень 3. Дизайн интерьеров",
          items: [
            "Квартиры",
            "Банные комплексы",
            "Торговые и бизнес-центры",
            "Промышленные объекты",
            "Гостиницы",
            "Рестораны и кафе",
          ],
        },
      ],
      images: [
        img(
          "archidica-industry-hotels",
          "Отраслевая страница проектирования отелей Archidica",
          1920,
          1246,
        ),
        img(
          "archidica-industry-bathhouse",
          "Страница проектирования банных комплексов Archidica",
          1920,
          1246,
        ),
        img(
          "archidica-industry-restaurants",
          "Отраслевая страница проектирования ресторанов Archidica",
          1920,
          1246,
        ),
      ],
      imageCols: 3,
      caption:
        "Каждое направление получает самостоятельную посадочную страницу, релевантные проекты и собственный сценарий обращения.",
    },
    {
      id: "forms",
      title: "Разные сценарии обращения для разных заказчиков",
      paragraphs: [
        "Для дорогостоящих архитектурных услуг недостаточно одной универсальной кнопки «Оставить заявку». На сайте предусмотрены разные точки входа: консультация, запись на встречу, получение индивидуального предложения и приглашение бюро к участию в тендере.",
      ],
      columns: [
        {
          title: "Форма «Приглашение в тендер»",
          items: [
            "Тип услуги",
            "Площадь объекта",
            "Адрес объекта",
            "Имя",
            "Телефон",
          ],
        },
        {
          title: "Варианты услуги в форме",
          items: ["Жилые интерьеры", "Коммерческие интерьеры", "Архитектурное проектирование"],
        },
        {
          title: "Контекстные CTA по разделам",
          items: [
            "Отели — «Обсудить проект отеля»",
            "Банные комплексы — «Получить консультацию по банному комплексу»",
            "Частная архитектура — «Обсудить проект дома»",
            "Общая страница — «Пригласить бюро в тендер»",
          ],
        },
      ],
      images: [
        img("archidica-tender-form", "Форма приглашения Archidica в тендер", 1920, 1246),
        img(
          "archidica-commercial-interiors",
          "Раздел отраслевой экспертизы на сайте Archidica",
          1920,
          1246,
        ),
      ],
      imageCols: 2,
    },
  ],

  story: [],
  features: [],

  design: {
    title: "Интерфейс, который не спорит с архитектурой",
    paragraphs: [
      "Визуальную систему построили вокруг самих проектов. Крупные изображения, выразительная типографика, свободное пространство и сдержанные элементы управления позволяют сосредоточить внимание на архитектуре и интерьерах.",
      "Интерфейс не пытается конкурировать с работами бюро. Он организует контент и помогает пользователю двигаться между проектами, направлениями и услугами.",
    ],
    bullets: [
      "Первый экран отдан слайдеру объектов: год, название и одна фраза о проекте вместо рекламного оффера.",
      "Карточки каталога построены на одинаковой сетке — тип объекта, название и короткое описание всегда на одном месте, поэтому проекты удобно сравнивать между собой.",
      "Меню разделено на две логики: портфолио по направлениям и отраслевая экспертиза, чтобы бизнес-заказчик не искал свой сценарий среди частных проектов.",
      "Состояния наведения и появление блоков при скролле сделаны сдержанными: анимация подсказывает границы карточек и не отвлекает от визуализаций.",
    ],
  },

  identity: { fonts: [], colors: [] },

  cover: img(
    "archidica-case-cover",
    "Обложка кейса Archidica: сайт архитектурного бюро на широком экране",
    1920,
    1080,
    6,
  ),
  coverVideo: vid.url,
  coverPoster: {
    src: `${dir}/archidica-home-scroll-poster.webp`,
    src900: `${dir}/archidica-home-scroll-poster-900.webp`,
    alt: "Скролл главной страницы сайта архитектурного бюро Archidica",
    width: 1920,
    height: 1080,
    span: 6,
    wide: true,
  },
  gallery: [],

  responsive: {
    text: [
      "Мобильная версия сохраняет визуальную выразительность проектов и при этом остаётся удобной для поиска нужной услуги. Каталог, фильтры, страницы объектов, галереи и формы адаптированы под управление с телефона.",
      "Фильтры каталога на телефоне сворачиваются в отдельный экран выбора, а карточки перестраиваются в одну колонку с крупной визуализацией объекта.",
      "Форма приглашения в тендер открывается на всю высоту экрана: тип услуги выбирается списком, а поля площади и адреса заполняются без переключения масштаба.",
    ],
    images: [
      img(
        "archidica-mobile-home-catalog",
        "Мобильная версия сайта Archidica: главная страница и каталог проектов",
        1920,
        1700,
        2,
        false,
      ),
      img(
        "archidica-mobile-project-villas",
        "Мобильная версия сайта Archidica: страница проекта виллы",
        1920,
        1700,
        2,
        false,
      ),
      img(
        "archidica-mobile-industry-contacts",
        "Мобильная версия сайта Archidica: отраслевая страница и контакты",
        1920,
        1700,
        2,
        false,
      ),
    ],
  },

  tech: {
    title: "Масштабируемая платформа на 1С-Битрикс",
    paragraphs: [
      "Сайт разработан на 1С-Битрикс. Административная система позволяет добавлять новые проекты, категории, сотрудников, публикации, отраслевые страницы и специальные предложения без изменения основной архитектуры сайта.",
    ],
    cards: [
      { title: "1С-Битрикс", text: "Платформа сайта и административная панель управления контентом." },
      { title: "Управление портфолио", text: "Добавление объектов, описаний, категорий и галерей визуализаций." },
      { title: "Динамические фильтры", text: "Подбор проектов по формату, типу работ и категории объекта." },
      { title: "SEO-настройки страниц", text: "Управление заголовками, метатегами и адресами разделов." },
      { title: "Адаптивная версия", text: "Каталог, страницы объектов и формы работают на смартфонах и планшетах." },
      { title: "Формы заявок", text: "Консультация, запись на встречу и приглашение бюро в тендер." },
      { title: "Веб-аналитика", text: "Сбор данных о поведении посетителей и обращениях с сайта." },
      { title: "Техническая поддержка", text: "Развитие разделов и сопровождение сайта после запуска." },
    ],
  },

  scale: {
    title: "Масштаб реализованной системы",
    paragraphs: [
      "Создали систему, которую можно последовательно расширять: добавлять новые проекты, отрасли, услуги и коммерческие посадочные страницы без полного пересмотра структуры сайта.",
    ],
    metrics: [
      { value: "100+", label: "опубликованных проектов в каталоге" },
      { value: "3", label: "параметра фильтрации: формат, тип работ, категория" },
      { value: "4", label: "основных направления портфолио" },
      { value: "19", label: "отраслевых и сервисных посадочных страниц" },
    ],
    note: "Данные отражают текущий масштаб опубликованного сайта.",
  },

  servicesDone: {
    title: "Что мы сделали для Archidica",
    items: [
      {
        label: "Аналитика и проектирование",
        to: routes.corporate,
        note: "Структура разделов, сценарии заказчиков и прототипы страниц",
      },
      {
        label: "UX/UI-дизайн",
        to: routes.corporate,
        note: "Визуальная система каталога, страниц объектов и отраслевых разделов",
      },
      {
        label: "Разработка корпоративного сайта",
        to: routes.corporate,
        note: "Основная услуга проекта",
      },
      {
        label: "Разработка на 1С-Битрикс",
        to: routes.bitrix24,
        note: "Административная система и управление контентом",
      },
      {
        label: "SEO-архитектура",
        to: routes.seo,
        note: "Разделение посадочных страниц по отраслям и направлениям",
      },
      { label: "SEO-продвижение", to: routes.seo, note: "Работа с поисковой видимостью разделов" },
      {
        label: "Техническая поддержка сайтов",
        to: routes.corporate,
        note: "Развитие и сопровождение проекта после запуска",
      },
      { label: "Веб-аналитика", to: routes.seo, note: "Настройка сбора данных по обращениям" },
    ],
  },
};
